import {
  IssueState,
  type IssueResolution,
  type PrototypeDeployStatus,
  type RunStatus,
  type StageStatus,
} from '@aiai/shared';
import { titleCase } from '../lib/format';
import { isPulsing, statusTone, RESOLUTION_TONE } from '../lib/statusTones';
import { Badge } from './Badge';

/**
 * Every status a badge can name: run, issue, stage, and prototype-deploy
 * states. They share one tone map (`statusTone`) and one label rule
 * (`titleCase`), so there is a single place a status→color+word decision lives.
 */
type AnyStatus = RunStatus | IssueState | StageStatus | PrototypeDeployStatus;

/** Row badges are the small size; detail pages use the default. */
export type BadgeSize = 'sm' | 'md';

const RESOLUTION_LABEL: Record<IssueResolution, string> = {
  fixed: 'Fixed',
  wont_do: "Won't do",
  duplicate: 'Duplicate',
  planned: 'Planned',
  decomposed: 'Decomposed',
};

export function StatusBadge({
  status,
  resolution,
  size = 'md',
  label,
}: {
  status: AnyStatus;
  /** Issue-only: when the issue is closed with a resolution, renders the resolution instead of "Closed". */
  resolution?: IssueResolution | null;
  size?: BadgeSize;
  label?: string;
}) {
  if (status === IssueState.Closed && resolution) {
    return (
      <Badge tone={RESOLUTION_TONE[resolution]} size={size} pulse={false}>
        {label ?? RESOLUTION_LABEL[resolution]}
      </Badge>
    );
  }

  return (
    <Badge tone={statusTone(status)} size={size} pulse={isPulsing(status)}>
      {label ?? titleCase(status)}
    </Badge>
  );
}

/**
 * Whether a loop is firing on its schedule. "Paused" rather than "Disabled":
 * the loop is intact and one toggle away from running, which is what the
 * operator is deciding between.
 */
export function LoopStateBadge({
  enabled,
  size = 'md',
}: {
  enabled: boolean;
  size?: BadgeSize;
}) {
  return (
    <Badge tone={enabled ? 'success' : 'neutral'} size={size}>
      {enabled ? 'Enabled' : 'Paused'}
    </Badge>
  );
}

/**
 * A project is a place, not a ticket — it has no lifecycle state to report, so
 * this renders only when there is something the operator can act on:
 * agents running now, or a missing GitHub token (which blocks clone/push/PR).
 * An idle, well-configured project gets no badge at all rather than a row of
 * "Idle" noise down the list.
 */
export function ProjectStateBadge({
  activeRuns,
  hasGithubCredential,
  size = 'md',
}: {
  activeRuns: number;
  hasGithubCredential: boolean;
  size?: BadgeSize;
}) {
  if (activeRuns > 0) {
    return (
      <Badge tone="brand" size={size} pulse>
        {activeRuns} running
      </Badge>
    );
  }
  if (!hasGithubCredential) {
    return (
      <Badge tone="danger" size={size}>
        No token
      </Badge>
    );
  }
  return null;
}
